import { type ReactNode, useEffect, useState } from "react";
import type { AdminApi } from "@/api";
import { Badge, Banner, Button, Textarea } from "@/components/ui";
import type {
  AnalysisContext, BatchDetections as Detections, DetectionReview, DetectionScan, DetectionSignal,
} from "@/types/issuePipeline";
import { JsonDetails, conversationHref, pipelineError } from "./pipelineShared";

const VERDICTS: Record<string, string> = {
  discomfort: "불편 확인",
  request_change: "변경 요청",
  other_target: "다른 대상",
  ai_not_invoked: "AI 미호출",
  insufficient_information: "정보 부족",
  no_issue: "문제 없음",
};
const SOURCE_KINDS: Record<string, string> = {
  negative_feedback: "👎 사용자",
  regenerate: "🔁 재생성",
  ai_observation: "AI 피드백",
  manual_promotion: "수동 승격",
};

export const kst = (value: unknown) =>
  typeof value === "string" ? new Date(value).toLocaleString("ko-KR", { timeZone: "Asia/Seoul" }) : "—";

const verdictLabel = (value: string | null | undefined) => (value ? VERDICTS[value] ?? value : "—");

export function reviewLabel(signal: DetectionSignal) {
  const review = signal.review;
  if (!review) return "검토 기록 없음";
  if (review.status === "failed") return "검토 실패";
  if (review.status !== "completed") return review.status;
  return VERDICTS[String(review.verdict)] ?? String(review.verdict);
}

/** The messages an analysis looked at, read from its source now (prod needs prod access). */
export function AnalysisConversation({ api, analysisId }: { api: AdminApi; analysisId: string }) {
  const [data, setData] = useState<AnalysisContext | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let live = true;
    api.getAnalysisContext(analysisId).then(
      (value) => live && setData(value),
      (err) => live && setError(pipelineError(err)),
    );
    return () => {
      live = false;
    };
  }, [api, analysisId]);
  if (error) return <Banner tone="error">{error}</Banner>;
  if (!data) return <p role="status" className="text-xs text-muted-foreground">대화 불러오는 중…</p>;
  if (!data.messages.length) return <p className="text-xs text-muted-foreground">원본에서 찾은 메시지가 없습니다.</p>;
  return (
    <div className="grid gap-1.5">
      <ol className="grid gap-2">
        {data.messages.map((message) => {
          const isAi = message.role === "assistant";
          return (
            <li
              key={message.id}
              className={`grid gap-1 rounded-lg border p-2.5 text-sm ${isAi ? "border-border bg-surface-subtle" : "border-border bg-card"} ${message.is_signal ? "border-accent-line" : ""}`}
            >
              <span className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                <span className="font-medium text-foreground">{isAi ? "AI" : "사용자"}</span>
                {message.is_signal && <Badge size="compact" variant="accent">1차 신호</Badge>}
                {message.changed && <Badge size="compact">탐지 후 수정됨</Badge>}
                {message.deleted && <Badge size="compact">삭제됨</Badge>}
                {kst(message.created_at)}
                <a
                  className="ml-auto text-link hover:underline"
                  href={conversationHref(data.source_environment, message.session_id, message.id)}
                >
                  메시지 열기 ↗
                </a>
              </span>
              <p className="line-clamp-6 break-words whitespace-pre-wrap">{message.content}</p>
            </li>
          );
        })}
      </ol>
      {(data.context_truncated || data.missing_message_ids.length > 0) && (
        <p className="text-xs text-muted-foreground">
          {data.context_truncated && "탐지 때 문맥 일부만 사용했습니다. "}
          {data.missing_message_ids.length > 0 && `원본에서 찾지 못한 메시지 ${data.missing_message_ids.length}개`}
        </p>
      )}
    </div>
  );
}

/** A signal the stages did not promote: no review, a failed one, or one that found no discomfort. */
export function promotable(signal: DetectionSignal) {
  if (signal.candidate_id) return false;
  const review = signal.review;
  if (!review) return Boolean(signal.scan_analysis_id);
  return review.status === "failed" || (review.status === "completed" && review.verdict !== "discomfort");
}

export function ManualPromote({ api, signal, onPromoted }: {
  api: AdminApi; signal: DetectionSignal; onPromoted: (candidateId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const analysisId = signal.review?.analysis_id ?? signal.scan_analysis_id;
  if (!analysisId) return null;
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const result = await api.manualPromote(analysisId, { reason: reason.trim(), signal_message_id: signal.signal_message_id });
      setOpen(false);
      onPromoted(result.candidate_id);
    } catch (err) {
      setError(pipelineError(err));
    } finally {
      setBusy(false);
    }
  };
  if (!open) {
    return (
      <div>
        <Button variant="outline" size="sm" onClick={() => setOpen(true)}>수동 승격</Button>
      </div>
    );
  }
  return (
    <div className="grid gap-1.5 rounded-lg border border-border bg-surface-subtle p-2.5">
      <label className="grid gap-1 text-xs">
        <span className="font-medium">승격 사유 (기록됨)</span>
        <Textarea rows={2} value={reason} onChange={(event) => setReason(event.target.value)} />
      </label>
      {error && <Banner tone="error">{error}</Banner>}
      <div className="flex flex-wrap gap-1.5">
        <Button size="sm" disabled={busy || !reason.trim()} onClick={submit}>1차 후보로 승격</Button>
        <Button variant="outline" size="sm" disabled={busy} onClick={() => setOpen(false)}>취소</Button>
      </div>
    </div>
  );
}

function Labeled({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid min-w-0 gap-0.5 rounded-lg border border-border p-2">
      <span className="text-[11px] font-medium text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}

/** The 1.1차 review: what the model said (AI 판단) next to what validation kept (검증 결과). */
export function ReviewJudgment({ review }: { review: DetectionReview }) {
  const ai = review.ai_judgment;
  const validation = review.validation;
  const failed = review.status === "failed";
  const issues = [...(validation?.errors ?? []), ...(validation?.normalizations ?? [])];
  return (
    <div className="grid gap-1.5 sm:grid-cols-2">
      <Labeled label="1.1차 · AI 판단">
        <span className="text-sm font-medium">{verdictLabel(ai?.verdict ?? review.model_verdict)}</span>
        <p className="break-words whitespace-pre-wrap text-xs">{ai?.reason ?? review.model_reason ?? "사유 없음"}</p>
        {ai?.source === "model_output" && (
          <span className="text-[11px] text-muted-foreground">모델 원본 출력에서 읽음</span>
        )}
        {ai?.parse_error && <span className="text-[11px] break-all text-destructive">{ai.parse_error}</span>}
      </Labeled>
      <Labeled label="1.1차 · 검증 결과">
        <span className="text-sm font-medium">
          {failed ? "검증 실패" : review.status === "completed" ? `통과 · ${verdictLabel(review.verdict)}` : review.status}
        </span>
        {!failed && review.reason && <p className="break-words whitespace-pre-wrap text-xs">{review.reason}</p>}
        {failed && (
          <pre className="max-h-40 overflow-auto text-xs break-all whitespace-pre-wrap text-destructive">
            {review.error ?? "사유 없음"}
          </pre>
        )}
        {issues.length > 0 && (
          <ul className="grid gap-0.5 text-[11px] break-all text-muted-foreground">
            {issues.map((item, index) => <li key={index}>{typeof item === "string" ? item : JSON.stringify(item)}</li>)}
          </ul>
        )}
      </Labeled>
    </div>
  );
}

function SignalItem({ api, signal, onOpenCandidate, onPromoted }: {
  api: AdminApi; signal: DetectionSignal; onOpenCandidate?: (id: string) => void; onPromoted: () => void;
}) {
  const [open, setOpen] = useState(false);
  const review = signal.review;
  const failed = review?.status === "failed";
  const contextId = review?.analysis_id ?? signal.scan_analysis_id;
  const manual = signal.candidate_issue_key === "manual_promotion";
  return (
    <li className="grid gap-1.5 rounded-lg border border-border p-2.5 text-sm">
      <span className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
        <Badge size="compact" variant={review?.verdict === "discomfort" && !failed ? "accent" : "default"}>
          {reviewLabel(signal)}
        </Badge>
        {signal.candidate_id && <Badge size="compact" variant="accent">{manual ? "수동 승격" : "1차 후보"}</Badge>}
        {signal.source_environment}
        {review && review.attempts > 1 && <span>· 시도 {review.attempts}회</span>}
        {signal.candidate_id && onOpenCandidate && (
          <button
            type="button"
            className="ml-auto text-link hover:underline"
            onClick={() => onOpenCandidate(signal.candidate_id as string)}
          >
            후보 열기 →
          </button>
        )}
      </span>
      <p className="break-words whitespace-pre-wrap">
        <span className="text-muted-foreground">1차 신호 · AI 판단 · </span>
        <span className="font-medium">{signal.reason ?? "사유 없음"}</span>
      </p>
      {review ? <ReviewJudgment review={review} /> : (
        <p className="text-xs text-muted-foreground">1.1차 검토 기록 없음</p>
      )}
      {promotable(signal) && <ManualPromote api={api} signal={signal} onPromoted={onPromoted} />}
      {contextId && (
        <div>
          <Button variant="outline" size="sm" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
            {open ? "대화 닫기" : "대화 보기"}
          </Button>
        </div>
      )}
      {open && contextId && <AnalysisConversation api={api} analysisId={contextId} />}
      {review && <JsonDetails title="검토 원본" value={review} />}
    </li>
  );
}

/** One 1차 scan: the signals it flagged, each with the model's reason; 신호 없음 needs no reason. */
function ScanItem({ api, scan }: { api: AdminApi; scan: DetectionScan }) {
  const [open, setOpen] = useState(false);
  const signals = scan.signals ?? [];
  return (
    <li className="grid gap-1 rounded-lg border border-border p-2 text-xs">
      <span className="flex flex-wrap items-center gap-1.5 text-muted-foreground">
        <Badge size="compact" variant={scan.status === "failed" ? "default" : signals.length ? "accent" : "default"}>
          {scan.status === "failed" ? "스캔 실패" : signals.length ? `신호 ${signals.length}` : "신호 없음"}
        </Badge>
        {kst(scan.completed_at)}
        {scan.attempts > 1 && <span>· 시도 {scan.attempts}회</span>}
        {scan.status === "completed" && (
          <button type="button" className="ml-auto text-link hover:underline" aria-expanded={open}
            onClick={() => setOpen((value) => !value)}>
            {open ? "메시지 닫기" : "본 메시지"}
          </button>
        )}
      </span>
      {scan.status === "failed" && <span className="break-all text-destructive">{scan.error ?? "사유 없음"}</span>}
      {signals.length > 0 && (
        <ul className="grid gap-1">
          {signals.map((item) => (
            <li key={item.message_id} className="break-words whitespace-pre-wrap text-sm">
              <span className="text-muted-foreground">AI 판단 · </span>
              <span className="font-medium">{item.reason ?? "사유 없음"}</span>
            </li>
          ))}
        </ul>
      )}
      {open && <AnalysisConversation api={api} analysisId={scan.analysis_id} />}
    </li>
  );
}

/** One run's 1st-pass scans and signals, their context reviews (failed ones too) and its candidates. */
export function BatchDetections({ api, batchId, onOpenCandidate }: {
  api: AdminApi; batchId: string; onOpenCandidate?: (id: string) => void;
}) {
  const [data, setData] = useState<Detections | null>(null);
  const [error, setError] = useState("");
  const [version, setVersion] = useState(0);
  useEffect(() => {
    let live = true;
    if (version === 0) setData(null);
    setError("");
    api.getBatchDetections(batchId).then(
      (value) => live && setData(value),
      (err) => live && setError(pipelineError(err)),
    );
    return () => {
      live = false;
    };
  }, [api, batchId, version]);

  if (error) return <Banner tone="error">{error}</Banner>;
  if (!data) return <p role="status" className="text-sm">불러오는 중…</p>;
  const settings = (data.batch.settings ?? {}) as Record<string, unknown>;
  const failedScans = data.scans.filter((scan) => scan.status === "failed").length;
  const quietScans = data.scans.filter((scan) => scan.status === "completed" && !scan.signal_count).length;
  const failedReviews = data.signals.filter((signal) => signal.review?.status === "failed").length;
  const reload = () => setVersion((value) => value + 1);
  return (
    <section aria-label="탐지 결과" className="grid gap-3 border-t border-border pt-3">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <h3 className="text-sm font-semibold">탐지 결과</h3>
        <span className="text-xs text-muted-foreground">
          {kst(data.batch.started_at)} · {String(settings.source_environment ?? "—")} · 1차 신호 {data.signals.length} ·
          검토 실패 {failedReviews} · 후보 {data.candidates.length}
        </span>
      </div>
      {typeof settings.last_context_error === "string" && (
        <p className="text-xs break-all text-destructive">마지막 검토 오류 · {settings.last_context_error}</p>
      )}
      {data.signals.length === 0 ? (
        <p className="text-sm text-muted-foreground">이 실행에서 찾은 1차 신호가 없습니다.</p>
      ) : (
        <ol className="grid gap-2">
          {data.signals.map((signal) => (
            <SignalItem key={signal.signal_message_id} api={api} signal={signal}
              onOpenCandidate={onOpenCandidate} onPromoted={reload} />
          ))}
        </ol>
      )}
      {data.scans.length > 0 && (
        <details className="text-sm" open={data.scans.length <= 5 || undefined}>
          <summary className="cursor-pointer text-muted-foreground">
            1차 판정 {data.scans.length}건 · 신호 없음 {quietScans} · 스캔 실패 {failedScans}
          </summary>
          <ul className="mt-2 grid gap-1.5">
            {data.scans.map((scan) => <ScanItem key={scan.analysis_id} api={api} scan={scan} />)}
          </ul>
        </details>
      )}
      <h4 className="text-sm font-semibold">후보 {data.candidates.length}</h4>
      {data.candidates.length === 0 ? (
        <p className="text-sm text-muted-foreground">이 실행에서 만든 후보가 없습니다.</p>
      ) : (
        <ul className="grid gap-2">
          {data.candidates.map((candidate) => (
            <li
              key={`${candidate.candidate_id}-${candidate.source_kind}`}
              className="grid gap-1 rounded-lg border border-border p-2.5 text-sm"
            >
              <span className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                <Badge size="compact" variant={candidate.manual ? "accent" : "default"}>
                  {SOURCE_KINDS[candidate.source_kind] ?? candidate.source_kind}
                </Badge>
                {candidate.problem_type} · {candidate.source_environment}
                {onOpenCandidate && (
                  <button
                    type="button"
                    className="ml-auto text-link hover:underline"
                    onClick={() => onOpenCandidate(candidate.candidate_id)}
                  >
                    후보 열기 →
                  </button>
                )}
              </span>
              <p className="line-clamp-3 break-words whitespace-pre-wrap">{candidate.description}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
